import { cloneDeep, isEqual, set } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DocumentNode, OperationVariables, TypedDocumentNode, useLazyQuery } from '@apollo/client';
import { TypeaheadOption } from './types';
import { FieldValue } from '../../../types';
import { useSmartFormContext } from '../../../forms/utils/useSmartFormContext';

export interface UseRecentItemsProps<TData, TVariables extends OperationVariables, TRaw, TValue> {
  /** The field's existing query document. The hook runs a `uids`-filtered copy of it internally. */
  query: DocumentNode | TypedDocumentNode<TData, TVariables>;
  name: string;
  /** Base query variables (without the recent uids). */
  variables: TVariables;
  /** Pluck the raw items array out of the query result. */
  selectItems: (data?: TData) => TRaw[];
  /** Map a raw item to a reconciled typeahead option — reuse the field's existing value mapper. */
  mapItem: (raw: TRaw) => TypeaheadOption<TValue>;
  /**
   * Dot-path at which the recent uids are injected into the query variables.
   * Defaults to a flat top-level `uids`. Override for nested filters, e.g.
   * `'query.uids'`, `'q.supplierUids'`, `'q.personUids'`.
   */
  uidsPath?: string;
  /**
   * Dot-path of the dropdown's free-text search field, e.g. `'query.search'` or `'q.search'`.
   * Cleared before the reconcile query runs so recent items are fetched by uid only — not
   * filtered by the live search term — which also keeps the reconcile variables stable while
   * the user types. Lets the field pass its dropdown `variables` as the single source of truth.
   */
  searchPath?: string;
  skip?: boolean;
  /**
   * Validate recent items against the server (dropping ids it no longer returns) even when the
   * `reconcileRecentItems` flag is OFF — without re-hydrating labels. Use for fields that already
   * relied on server validation before the flag existed (e.g. constraint-filtered Cost Centers,
   * SD-38700), so turning the flag off does not reintroduce stale/invalid recents.
   */
  alwaysValidate?: boolean;
}

/**
 * Reconciles the locally-stored "recent items" for a typeahead against current server data:
 * runs a `uids`-filtered copy of the field's query, preserves recency order, and drops items the
 * server no longer returns (deleted/invalid). When the `reconcileRecentItems` flag is on it also
 * carries the up-to-date label/code/value; otherwise it keeps the stored snapshot.
 *
 * Returns `undefined` (so {@link RecentItemsProvider} keeps its legacy localStorage behaviour) when
 * neither the flag nor `alwaysValidate` is active, or when the field is skipped.
 */
export const useRecentItems = <TData, TVariables extends OperationVariables, TRaw, TValue>({
  query,
  name,
  variables,
  selectItems,
  mapItem,
  uidsPath = 'uids',
  searchPath,
  skip,
  alwaysValidate
}: UseRecentItemsProps<TData, TVariables, TRaw, TValue>): TypeaheadOption<FieldValue>[] | undefined => {
  const { featureFlags } = useSmartFormContext();
  // `reconcile` re-hydrates labels (flag-gated); `active` also covers validate-only fields.
  const reconcile = !!featureFlags?.reconcileRecentItems;
  const active = (reconcile || !!alwaysValidate) && !skip;

  const [recentItems, setRecentItems] = useState<TypeaheadOption<FieldValue>[]>([]);
  const [filteredRecentItems, setFilteredRecentItems] = useState<TypeaheadOption<FieldValue>[]>([]);

  const RECENT_ITEM_KEY = `recent-items-update:${name}`;

  const [get] = useLazyQuery<TData, TVariables>(query, { fetchPolicy: 'no-cache', errorPolicy: 'all' });

  // Keep the mappers in refs so callers can pass inline functions without retriggering the
  // resolve effect (only the recent ids / variables should drive a refetch).
  const selectItemsRef = useRef(selectItems);
  selectItemsRef.current = selectItems;
  const mapItemRef = useRef(mapItem);
  mapItemRef.current = mapItem;

  // Load persisted snapshot + subscribe to cross-instance updates (with cleanup).
  useEffect(() => {
    const stored = localStorage.getItem(`recentItems:${name}`);
    if (stored) {
      try {
        setRecentItems(JSON.parse(stored) as TypeaheadOption<FieldValue>[]);
      } catch {
        // ignore malformed cache
      }
    }
    const syncItemChanged: EventListener = (event: Event) => {
      setRecentItems((event as CustomEvent<TypeaheadOption<FieldValue>[]>).detail);
    };
    window.addEventListener(RECENT_ITEM_KEY, syncItemChanged);
    return () => window.removeEventListener(RECENT_ITEM_KEY, syncItemChanged);
  }, [RECENT_ITEM_KEY, name]);

  const ids = useMemo(() => recentItems.map((item) => item.id), [recentItems]);

  // Stable variables reference so the resolve effect doesn't loop on object identity.
  // Clear the search field (recents are fetched by uid, not search) and inject the recent uids.
  const recentItemVariablesRef = useRef<TVariables>();
  const clonedVariables = cloneDeep(variables);
  if (searchPath) {
    set(clonedVariables, searchPath, undefined);
  }
  const nextRecentItemVariables = set(clonedVariables, uidsPath, ids);
  if (!isEqual(recentItemVariablesRef.current, nextRecentItemVariables)) {
    recentItemVariablesRef.current = nextRecentItemVariables;
  }
  const recentItemVariables = recentItemVariablesRef.current!;

  useEffect(() => {
    const resolve = async () => {
      const results = await get({ variables: recentItemVariables });
      const reconciledById = new Map<string, TypeaheadOption<FieldValue>>(
        selectItemsRef.current(results.data ?? undefined).map((raw) => {
          const option = mapItemRef.current(raw) as unknown as TypeaheadOption<FieldValue>;
          return [option.id, option];
        })
      );
      // Preserve localStorage recency order, drop items the server no longer returns. Carry the
      // reconciled (fresh) data only when the flag is on; otherwise keep the stored snapshot.
      setFilteredRecentItems(
        recentItems.reduce<TypeaheadOption<FieldValue>[]>((acc, item) => {
          const reconciled = reconciledById.get(item.id);
          if (reconciled) {
            acc.push(reconcile ? reconciled : item);
          }
          return acc;
        }, [])
      );
    };

    if (active && ids.length > 0) {
      resolve();
    } else if (ids.length === 0) {
      setFilteredRecentItems([]);
    }
  }, [active, reconcile, get, recentItems, recentItemVariables, ids.length]);

  return active ? filteredRecentItems : undefined;
};
